/**
 * Version stamps for the Conversational Money Map experience.
 *
 * These are deliberately decoupled from the plan / branch identifiers (e.g.
 * `goals-debt-v2`): bump the constant here to mark a meaningful change WITHOUT
 * renaming a branch. Both are stamped onto a thread (metrics) and onto the
 * generated artifact (results) so we can always see which version a member was
 * on in Grove.
 *
 * Use semver:
 * - CHAT: bump when the conversational flow changes — plans, objectives,
 *   prompts, ordering, widgets. Patch for copy tweaks, minor for behavior
 *   changes, major for a flow overhaul.
 * - ARTIFACT: bump when the generated results/artifact change — the money-map
 *   simulator, slide selection/logic, or the intake→artifact mapping. Patch for
 *   small display tweaks, minor for new/changed slides or numbers, major for a
 *   results overhaul.
 *
 * They move independently: a chat change need not bump the artifact and vice
 * versa.
 *
 * ─── When you bump a version, add a line to the matching changelog below ───
 */

/**
 * CHAT changelog (most recent first — add a line each time you bump):
 * - 3.0.0 — Monopath: goals-debt removed. The onboarding conversation now has a
 *   single member-facing path (goals-system); start-investing is retained in
 *   code (non-production only) but is no longer offered to members. The welcome
 *   branch carousel is replaced by a deterministic intro message ("Just a few
 *   quick questions…") plus a single-select "what best describes you?" menu;
 *   every answer (option or free-text) commits the goals-system branch and is
 *   carried along as member-notes context.
 * - 2.2.0 — Removed the info-cards carousel from the first response; the opener
 *   now goes straight from the intro text to the partner question. Partner
 *   widget title shortened to "Are you planning finances with a partner?".
 * - 2.1.0 — First tracked version.
 *
 *   Two paths: goals-debt & goals-system
 *
 *   PlanController is the only flow (legacy
 *   StepController / goals-branches removed); household (partner) question
 *   asked first; slider input for credit-card & personal-loan balances;
 *   savings no longer asks current saved (defaults to $0).
 */
export const CMM_CHAT_VERSION = '3.0.0';

/**
 * ARTIFACT changelog (most recent first — add a line each time you bump):
 * - 3.0.0 — Results/navigation overhaul (new artifact). The money-map results
 *   collapse from the linear 8-step walkthrough to three pages — "Your monthly
 *   budget" (Split), "Your automated system" (System), and the "Debt free by…"
 *   timeline — behind the initial AhaMoment intro. The per-box highlight steps
 *   (income/bills/spend/goals) fold into the single system page, and the
 *   guide-card Summary page is removed with its terminal CTA moved onto the
 *   timeline page.
 * - 2.0.0 — First tracked version. Results/artifact from the money-map
 *   simulator + slide logic + intake→artifact mapping.
 */
export const CMM_ARTIFACT_VERSION = '3.0.0';
